import { z } from 'zod';

/**
 * Where a report stands.
 *
 * Two states, and the second one carries its outcome in a separate field
 * ({@link ReportResolutionSchema}) for the reason `matches.endReason` is
 * separate from `matches.status`: "we acted" and "we decided there was nothing
 * to act on" are the same *stage* and very different *facts*, and a status that
 * encoded both would grow a value for every future outcome.
 *
 * - `pending_review` — filed, waiting for a human. This is what the queue opens
 *   on and what `moderation.autoHideReportThreshold` counts.
 * - `resolved` — a reviewer has decided. The row stays readable for ever: a
 *   decision nobody can find afterwards is a decision nobody can undo, which is
 *   the same argument the Trust queue makes for keeping its blocked rows.
 */
export const REPORT_STATUSES = ['pending_review', 'resolved'] as const;

export const ReportStatusSchema = z.enum(REPORT_STATUSES);

export type ReportStatus = (typeof REPORT_STATUSES)[number];

/** A report is filed waiting for a person; there is no draft. */
export const INITIAL_REPORT_STATUS: ReportStatus = 'pending_review';

/**
 * Does this status count toward the auto-hide threshold, and hold the pair's
 * slot in the partial unique index?
 *
 * The index that enforces "one open report per pair" is built from exactly this
 * predicate, so the index and this function are **one fact with two spellings**
 * — the discipline `isActiveMatchStatus` keeps for matches. A status counted
 * open here and omitted there would let one reporter file unboundedly many
 * reports and drive the threshold on their own.
 */
export function isOpenReportStatus(status: ReportStatus): boolean {
  return status === 'pending_review';
}
